import { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@contexts/AuthContext'
import { motion, AnimatePresence } from 'motion/react'
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  Camera,
  Package,
  DollarSign,
  Calendar
} from 'lucide-react'
import { BASE_PHOTO_PRICES, PHOTO_VIDEO_PRICES, VIDEO_ONLY_PRICES } from '@/lib/constants/packagePricing'
import { loadBookingRules } from '@hooks/useBookingRules.js'
import { DEFAULT_BOOKING_RULES, lateFeePerMemberText } from '@/lib/booking/bookingRules.js'

// Knowledge Base - Grace's Database for Love & Photos (prices come from the one ladder, not typed here)
const GRACE_KNOWLEDGE = {
  name: "Grace",
  role: "Wedding Consultant",
  company: "Love & Photos",

  packages: {
    photoOnly: BASE_PHOTO_PRICES,
    photoVideo: PHOTO_VIDEO_PRICES,
    videoOnly: VIDEO_ONLY_PRICES
  },

  addons: {
    rawFootage: { price: 395, name: "Raw Footage", popular: true },
    insurance: { price: 395, name: "Insured Photographer", chosen: "94%" },
    dateChange: { price: 195, priceAfter: 495, name: "One-Time Date Change" },
    rushDelivery: { price: 650, name: "Rush Delivery" }
  },

  secondPhotographer: {
    photoOnlyRate: 100, // per hour
    photoVideoRate: "50% of total package price",
    minimumHours: 4
  },

  lateBookingFee: {
    threshold: "1 month",
    feePerPerson: 450
  },

  delivery: {
    photos: "up to 90 days",
    video: "up to 4 months"
  },

  policies: {
    refunds: false,
    servicesUSAOnly: true,
    noHeavyPhotoshop: true,
    noTipping: true,
    noTax: true,
    processingFee: 150
  },

  social: {
    instagram: "instagram.com/lovesandphotos",
    facebook: "facebook.com/profile.php?id=61573197380793",
    theKnot: "theknot.com/marketplace/love-and-photos-springfield-mo-2092546"
  }
}

// Grace's Response Templates - Casual, warm, text-style
const GRACE_RESPONSES = {
  greeting: [
    "Hey! I'm Grace, your wedding consultant for Love & Photos. What can I help you with today?",
    "Hi there! Grace here. I can answer pretty much anything about your wedding coverage. What's on your mind?",
    "Hello! I'm Grace and I'm here to help with your wedding photography and video. What would you like to know?"
  ],

  pricing: (hours = 6) => {
    const photo = GRACE_KNOWLEDGE.packages.photoOnly[hours] || GRACE_KNOWLEDGE.packages.photoOnly[6]
    const photoVideo = GRACE_KNOWLEDGE.packages.photoVideo[hours] || GRACE_KNOWLEDGE.packages.photoVideo[6]
    return `For ${hours} hours:
• Photo only: $${photo}
• Photo + Video: $${photoVideo}

Want to know more about what's included, add-ons, or the booking process?`
  },

  pricingGeneral: `Packages start at $${BASE_PHOTO_PRICES[1]} for photo, or $${PHOTO_VIDEO_PRICES[1].toLocaleString('en-US')} for photo + video. Most couples book 6-8 hours.

Want me to show you pricing for a specific number of hours?`,

  videoOnly: `Yes, video-only starts at $850 for one hour.

Here's the breakdown:
• 1-4 hrs: $850-$1,450
• 5-8 hrs: $1,650-$2,250
• 9-12 hrs: $2,450-$3,250

How many hours are you thinking?`,

  photoVideoIncluded: `You'll have both a photographer and videographer, hundreds of edited photos, and a 5-10 minute highlight film.

Everything gets delivered in a private online gallery that's easy to download, share, and print.`,

  turnaround: `About 90 days for photos, 4 months for video. Rush options available if you want everything faster.`,

  rushDelivery: `Rush delivery is $650 and gets you everything much faster - usually within 2-4 weeks instead of months. Great if you need your photos and video ASAP!`,

  rawFootage: `Yes, you can add it for $395. It includes all photos and video, unfiltered and unedited. It's our most popular add-on, most couples love having it all to look back on.`,

  meetPhotographer: `You won't meet them before the wedding, but you can message your photographer a month before through our online chat to share your details.`,

  usaOnly: `No, we only service weddings within the USA.`,

  dateChange: `Yes, you can add a one-time date change for $50 during booking, or $495 later. Peace of mind just in case.`,

  photoshop: `Nope, just natural retouching to balance color and light.`,

  tipping: `No tipping needed.`,

  travelFees: `If there are any, they'll be listed before you book.`,

  insurance: `Yes, you can add it as an option in your profile if your venue requires a COI. It's $395 and chosen by 94% of couples. Highly recommended.`,

  engagementShoot: `Yes! Engagement shoots are available as an add-on and might be shot by a different photographer depending on availability.`,

  discount: `Our prices are already discounted, but everything's upfront and all add-ons are optional.`,

  addons: `Here are the popular add-ons:

• $395 Raw Footage (most popular!)
• $395 Insured Photographer (94% choose this)
• $195 One-Time Date Change ($495 after booking)
• $650 Rush Delivery
• Second Photographer (starts at $100/hr for photo)

Want to know more about any of these?`,

  secondPhotographer: `Great for larger weddings or just more coverage. Must be booked for at least 4 hours, and the hours must match your main package.

• Photo-only: $100 per hour
• Photo + Video: 50% of total package price`,


  booking: `You'll sign your contract online during booking before paying. You can save your date instantly after choosing your package.

Want to get started?`,

  payments: `Monthly payment plans and deposit options available. There's a $150 processing fee for payment plans.

No tax, no tipping. All transactions are final though.`,

  refunds: `All transactions are final. We don't offer refunds once booked.`,

  aiReveal: `I'm actually an AI assistant for Love & Photos, but I can help you with almost anything about your wedding coverage.`,

  rude: `No problem, wishing you the best with your wedding planning.`,

  offTopic: `I might not have info on that, but I can help with almost anything around your wedding coverage.`,

  default: `I can help with:

• Package pricing for photo, video, or both
• Add-ons like raw footage and insurance
• Booking process and payment plans
• Delivery times and policies

What would you like to know?`
}

// Suggestion chips for common queries
const SUGGESTION_CHIPS = [
  { label: "Pricing", icon: DollarSign },
  { label: "What's included?", icon: Package },
  { label: "Video options", icon: Camera },
  { label: "Add-ons", icon: Sparkles },
  { label: "How to book", icon: Calendar }
]

export default function ChatAssistant() {
  const { user } = useAuth()
  const { pathname } = useLocation()
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [inputValue, setInputValue] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [hasInteracted, setHasInteracted] = useState(false)
  const [isRudeMode, setIsRudeMode] = useState(false)
  const messagesEndRef = useRef(null)
  const lateRules = useRef(DEFAULT_BOOKING_RULES)
  useEffect(() => { loadBookingRules().then(r => { lateRules.current = r }) }, [])
  const inputRef = useRef(null)

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Auto-focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Show greeting after delay on first open
  useEffect(() => {
    if (isOpen && !hasInteracted) {
      setHasInteracted(true)
      setTimeout(() => {
        addBotMessage(GRACE_RESPONSES.greeting[0])
      }, 500)
    }
  }, [isOpen, hasInteracted])

  // Add bot message
  const addBotMessage = (content, chips = []) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content,
      chips,
      timestamp: new Date()
    }])
  }

  // Add user message
  const addUserMessage = (content) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content,
      timestamp: new Date()
    }])
  }

  // Check if user is being rude or uninterested
  const isRudeOrUninterested = (input) => {
    const lower = input.toLowerCase()
    const rudeWords = ['fuck', 'shit', 'stupid', 'dumb', 'idiot', 'scam', 'waste', 'useless']
    const uninterestedPhrases = ['not interested', 'no thanks', 'don\'t care', 'leave me alone', 'stop']

    return rudeWords.some(word => lower.includes(word)) ||
           uninterestedPhrases.some(phrase => lower.includes(phrase))
  }

  // Extract hours from user input
  const extractHours = (input) => {
    const match = input.match(/(\d+)\s*(?:hour|hr)/i)
    if (match) {
      const hours = parseInt(match[1])
      return hours >= 1 && hours <= 12 ? hours : null
    }
    return null
  }

  // Process user input and generate response - Grace's brain
  const processUserInput = useCallback((input) => {
    const lowerInput = input.toLowerCase()

    // Check for rudeness - Grace stops responding
    if (isRudeOrUninterested(input)) {
      setIsRudeMode(true)
      return { response: GRACE_RESPONSES.rude, chips: [], final: true }
    }

    // If already in rude mode, don't respond
    if (isRudeMode) {
      return { response: "", chips: [], silent: true }
    }

    // Check if asking if she's real/AI
    if ((lowerInput.includes('are you') || lowerInput.includes('you are')) &&
        (lowerInput.includes('real') || lowerInput.includes('ai') || lowerInput.includes('bot') || lowerInput.includes('human'))) {
      return { response: GRACE_RESPONSES.aiReveal, chips: [] }
    }

    // Check if user is just typing a number of hours (e.g., "6 hours", "8 hours")
    const hours = extractHours(lowerInput)
    if (hours && lowerInput.match(/^\s*\d+\s*(?:hour|hr)s?\s*$/i)) {
      // User typed just "X hours" - show pricing + menu
      return {
        response: GRACE_RESPONSES.pricing(hours) + '\n\n' + GRACE_RESPONSES.default,
        chips: SUGGESTION_CHIPS.map(chip => ({
          label: chip.label,
          custom: chip.label
        }))
      }
    }

    // Pricing queries
    if (lowerInput.includes('cost') || lowerInput.includes('price') || lowerInput.includes('pricing') || lowerInput.includes('how much')) {
      const hours = extractHours(lowerInput)

      if (lowerInput.includes('video') && !lowerInput.includes('photo')) {
        return { response: GRACE_RESPONSES.videoOnly, chips: [] }
      }

      if (hours) {
        // Show pricing for specific hours, then offer the main menu
        return {
          response: GRACE_RESPONSES.pricing(hours) + '\n\n' + GRACE_RESPONSES.default,
          chips: SUGGESTION_CHIPS.map(chip => ({
            label: chip.label,
            custom: chip.label
          }))
        }
      }

      return { response: GRACE_RESPONSES.pricingGeneral, chips: [
        { label: "6 hours", custom: "6 hours" },
        { label: "8 hours", custom: "8 hours" },
        { label: "10 hours", custom: "10 hours" }
      ]}
    }

    // Video-only inquiries
    if ((lowerInput.includes('just video') || lowerInput.includes('only video') || lowerInput.includes('video only')) &&
        !lowerInput.includes('photo')) {
      return {
        response: GRACE_RESPONSES.videoOnly,
        chips: [
          { label: "4 hours", custom: "4 hours video" },
          { label: "6 hours", custom: "6 hours video" },
          { label: "8 hours", custom: "8 hours video" }
        ]
      }
    }

    // What's included inquiries
    if ((lowerInput.includes('what') || lowerInput.includes('whats')) &&
        (lowerInput.includes('included') || lowerInput.includes('include') || lowerInput.includes('get'))) {
      return {
        response: GRACE_RESPONSES.photoVideoIncluded,
        chips: [
          { label: "How much does it cost?", custom: "pricing" },
          { label: "Video options", custom: "video options" },
          { label: "Add-ons", custom: "add-ons" }
        ]
      }
    }

    // Turnaround/delivery time
    if (lowerInput.includes('turnaround') || lowerInput.includes('delivery') ||
        lowerInput.includes('how long') || lowerInput.includes('when will i get')) {
      return {
        response: GRACE_RESPONSES.turnaround,
        chips: [
          { label: "Rush delivery option", custom: "rush delivery" },
          { label: "See pricing", custom: "pricing" },
          { label: "How to book", custom: "how to book" }
        ]
      }
    }

    // Raw footage
    if (lowerInput.includes('raw') || (lowerInput.includes('all') && lowerInput.includes('footage'))) {
      return { response: GRACE_RESPONSES.rawFootage, chips: [] }
    }

    // Meet photographer
    if (lowerInput.includes('meet') && lowerInput.includes('photographer')) {
      return { response: GRACE_RESPONSES.meetPhotographer, chips: [] }
    }

    // International/outside US
    if (lowerInput.includes('international') || lowerInput.includes('outside') ||
        lowerInput.includes('out of') || lowerInput.includes('abroad')) {
      return { response: GRACE_RESPONSES.usaOnly, chips: [] }
    }

    // Date change
    if (lowerInput.includes('date') && (lowerInput.includes('change') || lowerInput.includes('reschedule'))) {
      return { response: GRACE_RESPONSES.dateChange, chips: [] }
    }

    // Photoshop/editing
    if (lowerInput.includes('photoshop') || lowerInput.includes('edit') || lowerInput.includes('retouch')) {
      return { response: GRACE_RESPONSES.photoshop, chips: [] }
    }

    // Tipping
    if (lowerInput.includes('tip') && !lowerInput.includes('multiple')) {
      return { response: GRACE_RESPONSES.tipping, chips: [] }
    }

    // Travel fees
    if (lowerInput.includes('travel') && (lowerInput.includes('fee') || lowerInput.includes('charge'))) {
      return { response: GRACE_RESPONSES.travelFees, chips: [] }
    }

    // Insurance
    if (lowerInput.includes('insurance') || lowerInput.includes('coi') || lowerInput.includes('certificate')) {
      return { response: GRACE_RESPONSES.insurance, chips: [] }
    }

    // Engagement shoot
    if (lowerInput.includes('engagement') && (lowerInput.includes('shoot') || lowerInput.includes('session'))) {
      return { response: GRACE_RESPONSES.engagementShoot, chips: [] }
    }

    // Discount requests
    if (lowerInput.includes('discount') || lowerInput.includes('deal') ||
        lowerInput.includes('cheaper') || lowerInput.includes('lower price')) {
      return { response: GRACE_RESPONSES.discount, chips: [] }
    }

    // Add-ons
    if (lowerInput.includes('addon') || lowerInput.includes('add-on') || lowerInput.includes('add on') ||
        lowerInput.includes('upgrade') || lowerInput.includes('extra')) {
      if (lowerInput.includes('second') && lowerInput.includes('photographer')) {
        return { response: GRACE_RESPONSES.secondPhotographer, chips: [] }
      }
      return {
        response: GRACE_RESPONSES.addons,
        chips: [
          { label: "Raw footage", custom: "raw footage" },
          { label: "Insurance", custom: "insurance" },
          { label: "Second photographer", custom: "second photographer" }
        ]
      }
    }

    // Second photographer
    if (lowerInput.includes('second') && lowerInput.includes('photographer')) {
      return { response: GRACE_RESPONSES.secondPhotographer, chips: [] }
    }

    // Late booking
    if (lowerInput.includes('late') || (lowerInput.includes('less than') && lowerInput.includes('month'))) {
      // The late rule in force (Business Controls → Booking Rules)
      const r = lateRules.current
      return { response: `If your wedding is ${r.lateWindowDays} days away or less, there's a ${lateFeePerMemberText(r)} late booking fee per team member. So if you're booking both a photographer and videographer, it's +$${(r.lateFeePerMemberCents * 2 / 100).toLocaleString('en-US')} total.`, chips: [] }
    }

    // Booking process
    if (lowerInput.includes('book') || lowerInput.includes('reserve') || lowerInput.includes('how to')) {
      return { response: GRACE_RESPONSES.booking, chips: [
        { label: "Start booking", action: "/#negotiation-widget" }
      ]}
    }

    // Payment plans
    if (lowerInput.includes('payment') && (lowerInput.includes('plan') || lowerInput.includes('monthly'))) {
      return { response: GRACE_RESPONSES.payments, chips: [] }
    }

    // Refunds/cancellation
    if (lowerInput.includes('refund') || lowerInput.includes('cancel') || lowerInput.includes('money back')) {
      return { response: GRACE_RESPONSES.refunds, chips: [] }
    }

    // Video-related (catch-all for "video options", "video", etc.)
    if (lowerInput.includes('video')) {
      return {
        response: GRACE_RESPONSES.videoOnly,
        chips: [
          { label: "4 hours", custom: "4 hours video" },
          { label: "6 hours", custom: "6 hours video" },
          { label: "8 hours", custom: "8 hours video" }
        ]
      }
    }

    // Rush delivery
    if (lowerInput.includes('rush')) {
      return {
        response: GRACE_RESPONSES.rushDelivery,
        chips: [
          { label: "Other add-ons", custom: "add-ons" },
          { label: "See pricing", custom: "pricing" },
          { label: "How to book", custom: "how to book" }
        ]
      }
    }

    // Default helpful response
    return { response: GRACE_RESPONSES.default, chips: SUGGESTION_CHIPS.map(chip => ({
      label: chip.label,
      custom: chip.label
    }))}
  }, [isRudeMode])

  // Handle message submission
  const handleSubmit = (e) => {
    e?.preventDefault()

    if (!inputValue.trim()) return
    if (isRudeMode) return

    // Add user message
    addUserMessage(inputValue)
    const userInput = inputValue
    setInputValue('')

    // Show typing indicator
    setIsTyping(true)

    // Simulate processing time (shorter, more human-like)
    setTimeout(() => {
      const result = processUserInput(userInput)
      setIsTyping(false)

      if (!result.silent) {
        addBotMessage(result.response, result.chips)
      }
    }, 800 + Math.random() * 400)
  }

  // Handle suggestion chip click
  const handleChipClick = (chip) => {
    if (chip.action) {
      window.location.href = chip.action
    } else if (chip.custom) {
      setInputValue(chip.custom)
      setTimeout(() => handleSubmit(), 100)
    }
  }

  // V26: Grace shows to everyone on the homepage and to signed-out visitors elsewhere; a signed-in visitor off the
  // homepage has the messages bubble instead.
  if (user && pathname !== '/') return null

  return (
    <>
      {/* Chat Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-24 right-6 z-40 flex items-center gap-2 bg-gradient-to-r from-[#fe395f] to-[#ff7a9a] text-white px-4 py-3 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300"
            aria-label="Chat with Grace"
            title="Chat with Grace, your wedding consultant"
          >
            <MessageCircle className="h-5 w-5" />
            <span className="hidden sm:inline font-medium">Chat with Grace</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 20 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#fe395f] to-[#ff7a9a] p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-11 h-11 bg-white rounded-full flex items-center justify-center overflow-hidden border-2 border-white/50 shadow-lg">
                    <Camera className="w-6 h-6 text-[#fe395f]" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse" />
                </div>
                <div>
                  <h3 className="font-semibold">Grace</h3>
                  <p className="text-xs text-white/80">Wedding Consultant</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 bg-white hover:bg-white/90 rounded-full transition-all duration-200 hover:scale-110 shadow-lg"
                aria-label="Close chat"
              >
                <X className="h-5 w-5 stroke-[3] text-[#fe395f]" />
              </button>
            </div>

            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-gray-800">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-2 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {/* Bot Avatar */}
                  {message.type === 'bot' && (
                    <div className="w-8 h-8 bg-gradient-to-br from-[#fe395f]/10 to-[#ff7a9a]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-[#fe395f]/20">
                      <Camera className="w-4 h-4 text-[#fe395f]" />
                    </div>
                  )}

                  <div className={`max-w-[80%] ${message.type === 'user'
                    ? 'bg-[#fe395f] text-white rounded-2xl rounded-br-md'
                    : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-2xl rounded-bl-md shadow-sm'
                  } px-4 py-3`}>
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    {/* Action Chips */}
                    {message.chips && message.chips.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {message.chips.map((chip, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleChipClick(chip)}
                            className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-500 transition-colors"
                          >
                            {chip.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex gap-2 justify-start"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-[#fe395f]/10 to-[#ff7a9a]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm border border-[#fe395f]/20">
                    <Camera className="w-4 h-4 text-[#fe395f]" />
                  </div>
                  <div className="bg-white dark:bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex gap-1">
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                        className="w-2 h-2 bg-gray-400 rounded-full"
                      />
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }}
                        className="w-2 h-2 bg-gray-400 rounded-full"
                      />
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }}
                        className="w-2 h-2 bg-gray-400 rounded-full"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Suggestion Chips */}
            {messages.length === 1 && (
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
                <div className="flex flex-wrap gap-2">
                  {SUGGESTION_CHIPS.map((chip, idx) => {
                    const Icon = chip.icon
                    return (
                      <button
                        key={idx}
                        onClick={() => {
                          setInputValue(chip.label)
                          handleSubmit()
                        }}
                        className="flex items-center gap-1.5 text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Icon className="h-3 w-3" />
                        {chip.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Ask Grace anything..."
                  disabled={isRudeMode}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#fe395f]/50 focus:border-[#fe395f] disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Chat message input"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isRudeMode}
                  className="p-2.5 bg-[#fe395f] text-white rounded-full hover:bg-[#fe395f]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
